import { map } from 'lodash'
import type { RExternalOrgApp } from 'v2/types/api/settings'

const providerAliases: Record<string, string> = {
  'google-mail': 'gmail',
  gmail: 'gmail',
  'google-sheet': 'google-sheets',
  'google-sheets': 'google-sheets',
  'cal-com-v2': 'cal',
  cal: 'cal',
  zoho: 'zoho-crm',
  zohocrm: 'zoho-crm',
  'zoho-crm': 'zoho-crm',
  zohodesk: 'zoho-desk',
  'zoho-desk': 'zoho-desk',
  zohopeople: 'zoho-people',
  'zoho-people': 'zoho-people',
}

/**
 * Canonical provider key across the two naming schemes still in play: legacy
 * rows keep their Nango provider key ("google-mail"), new-service rows and the
 * catalog derive a slug from the app name (GMAIL → "gmail"). Any code matching
 * a legacy connection against new-service data must compare through this,
 * never with raw provider equality.
 */
export const providerKey = (provider: string) => {
  const normalizedProvider = provider.toLowerCase().replaceAll('_', '-')
  return providerAliases[normalizedProvider] ?? normalizedProvider
}

interface LoadIntegrationConnectionsArgs {
  rolloutEnabled: boolean
  fetchAci: () => Promise<RExternalOrgApp[]>
  fetchLegacy: () => Promise<RExternalOrgApp[]>
}

export interface IntegrationConnectionLoadResult {
  connections: RExternalOrgApp[]
}

/**
 * Loads the org's connections from exactly ONE source. The rollout flag is a
 * replacement switch, not a blend: on means the new service owns the whole
 * surface, off means legacy does. Listing both at once rendered the same
 * provider twice — one connected, one "Reconnect required" — leaving no way to
 * tell which one an agent would actually call.
 *
 * A failure therefore has no fallback and propagates to the caller: serving the
 * other system's connections would route execution to the side the flag says is
 * off.
 */
export const loadIntegrationConnections = async ({
  rolloutEnabled,
  fetchAci,
  fetchLegacy,
}: LoadIntegrationConnectionsArgs): Promise<IntegrationConnectionLoadResult> => {
  const connections = rolloutEnabled ? await fetchAci() : await fetchLegacy()

  return {
    connections: map(connections, (connection) => ({
      ...connection,
      integration_source: rolloutEnabled ? 'aci' : 'nango',
    })),
  }
}
